import { ChildProps } from '@apollo/client/react/hoc';
import React from 'react';
import { ExecutionResult } from 'graphql';
import { times } from 'lodash';
import { connect } from '@reverbdotcom/commons/src/connect';
import { withGraphql } from '@reverbdotcom/commons/src/with_graphql';
import { CoreFindFavorite, DeleteMyFavorite } from '@reverbdotcom/commons/src/gql/graphql';
import { FavoriteButton } from '../../favorites/favorite_button';
import { EntityType } from '@reverbdotcom/commons/src/favorites/entity_type';
import { FIND_FAVORITE_QUERY } from '../../favorites/find_favorite_context';
import { MParticleEventName } from '@reverbdotcom/commons/src/elog/mparticle_types';
import { Paths } from '@reverbdotcom/commons/src/url_helpers';
import { RCCard } from '@reverbdotcom/cadence/components';
import { trackEvent } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import classNames from 'classnames';

interface IExternalProps {
  search: any;
  idx: number;
  handleModalOpen: (anything) => void;
  handleFavoriteRemove: (number, result: ExecutionResult<DeleteMyFavorite.Mutation>) => void;
}

export type IProps = ChildProps<IExternalProps, CoreFindFavorite.Query>;

const COMPONENT_NAME = 'HomepageSearchRecommendation';
const IMAGES_COUNT = 2;

function organizeImages(listings) {
  const images = [];
  if (!listings) { return images; }
  times(IMAGES_COUNT, (idx) => {
    listings.forEach(listing => {
      if (images.length === IMAGES_COUNT) { return images; }
      if (listing?.images?.length > idx) {
        images.push(listing.images[idx]);
      }
    });
  });

  return images;
}

function linkTo(search) {
  return (
    Paths.marketplace.expand({
      query: search?.query,
      product_type: search?.categorySlug,
      referer: 'trending_search',
    })
  );
}

export function SearchRecommendation(props: IProps) {
  const { search, idx, data, handleModalOpen } = props;
  const images = organizeImages(search?.listings);

  const handleRemoveFavorite = (result: ExecutionResult<DeleteMyFavorite.Mutation>) => {
    props.handleFavoriteRemove(idx, result);
  };

  const findFavorite = data?.findFavorite;
  let favorited = findFavorite?.favorite?.favorited;
  if (search?.favorite) { favorited = search?.favorite?.favorited; }
  const titleClasses = classNames(
    'search_recommendation__title',
    'color-black',
    'weight-bold',
    'size-100',
    'mb-1',
  );

  if (!search) { return null; }

  return (
    <RCCard>
      <RCCard.LinkWrapper>
        <a
          onClick={() => trackEvent({
            eventName: MParticleEventName.ClickedSearchRecommendationTile,
            componentName: COMPONENT_NAME,
            query: search?.query,
          })}
          href={linkTo(search)}
          key={`homepage-search-recommendation-${idx}`}
        >
          <div className="bd-1 bd--offwhite bd-radius-xl overflow-hidden">
            <div className="pos-relative padding-4">
              <div className="pos-absolute pos-top-2 pos-right-2">
                <FavoriteButton
                  renderWithBanner={true}
                  entityType={EntityType.Search}
                  openModal={() => handleModalOpen(search) }
                  canFavorite={findFavorite?.canFavorite}
                  isFavorited={favorited}
                  favoriteId={search?.favorite?.id || findFavorite?.favorite?.id}
                  handleFavoriteRemove={handleRemoveFavorite}
                  condensed
                  borderless
                />
              </div>
              <div className="pr-4">
                <div className={titleClasses}>
                  {search?.query}
                </div>
                <div className="color-orange weight-bold">
                  {search?.totalText}
                </div>
              </div>
            </div>
            <div className="d-grid grid-cols-2">
              {times(IMAGES_COUNT, (listingIdx) => {
                return (
                  <div className="grid-col-span-1" key={`search-rec-photo-${listingIdx}`} >
                    <div className="image-box">
                      <img src={images[listingIdx]?.source} alt="" />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </a>
      </RCCard.LinkWrapper>
    </RCCard>
  );
}

const withFindFavoriteQuery =
  withGraphql<
    IProps,
    CoreFindFavorite.Query,
    CoreFindFavorite.Variables
  >(
    FIND_FAVORITE_QUERY,
    {
      options: (props: IProps) => {
        return {
          ssr: false,
          variables: {
            listingsSearchRequest: {
              query: props.search?.query,
            },
          },
          fetchPolicy: 'network-only',
        };
      },
    },
  );

export default connect<IProps>([withFindFavoriteQuery])(SearchRecommendation);
